/**
 * Create and edit municipality role – dedicated page with permissions.
 *
 * Phase 2.3–2.4 – USER_ROLES.md
 */

import React, { useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Form, Input, Button, Card, Space, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  useMunicipalityRole,
  useCreateMunicipalityRole,
  useUpdateMunicipalityRole,
} from '../hooks/useMunicipalityRoles';
import { RolePermissionsEditor } from '../components/userRoles';
import type { RolePermissions } from '@municipality/types';
import { assertValidRoleId, ROLE_ID_REGEX } from '@municipality/types';

const { TextArea } = Input;

export const RoleForm: React.FC = () => {
  const { t } = useTranslation(['userRoles', 'common']);
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isEdit = Boolean(id);

  const [form] = Form.useForm<{
    name: string;
    roleId: string;
    description?: string;
    permissions: RolePermissions;
  }>();

  const { data: role, isLoading } = useMunicipalityRole(id ?? null);
  const createMutation = useCreateMunicipalityRole();
  const updateMutation = useUpdateMunicipalityRole();

  useEffect(() => {
    if (role) {
      form.setFieldsValue({
        name: role.name,
        roleId: role.id,
        description: role.description ?? '',
        permissions: role.permissions ?? {},
      });
    }
  }, [role, form]);

  const handleSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const permissions = (values.permissions ?? {}) as RolePermissions;

      if (isEdit && id) {
        await updateMutation.mutateAsync({
          roleId: id,
          input: {
            name: values.name,
            description: values.description || undefined,
            permissions,
          },
        });
        message.success(t('userRoles:form.updateSuccess'));
      } else {
        const roleId = (values.roleId ?? '').trim().toLowerCase().replace(/[^a-z0-9_]/g, '_');
        assertValidRoleId(roleId, 'Role ID');
        await createMutation.mutateAsync({
          input: {
            id: roleId,
            name: values.name,
            description: values.description || undefined,
            isSystem: false,
            permissions,
          },
          createdBy: user?.uid,
        });
        message.success(t('userRoles:form.createSuccess'));
      }
      navigate('/user-roles');
    } catch (err) {
      if (err instanceof Error && !err.message.includes('validateFields')) {
        message.error(isEdit ? t('userRoles:form.updateError') : t('userRoles:form.createError'));
      }
    }
  }, [isEdit, id, form, createMutation, updateMutation, user?.uid, navigate, t]);

  if (isEdit && isLoading) {
    return <Card loading />;
  }

  if (isEdit && id && !role) {
    message.error(t('userRoles:form.updateError'));
    navigate('/user-roles');
    return null;
  }

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0 }}>
          {isEdit ? t('userRoles:form.editTitle') : t('userRoles:form.createTitle')}
        </h2>
      </div>

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          name: '',
          roleId: '',
          description: '',
          permissions: {},
        }}
      >
        <Card style={{ marginBottom: 24 }}>
          <Form.Item
            name="name"
            label={t('userRoles:form.name')}
            rules={[{ required: true, message: t('userRoles:form.nameRequired') }]}
          >
            <Input placeholder={t('userRoles:form.namePlaceholder')} />
          </Form.Item>

          {!isEdit && (
            <Form.Item
              name="roleId"
              label={t('userRoles:form.id')}
              extra={t('userRoles:form.idHint')}
              rules={[
                { required: true, message: t('userRoles:form.idRequired') },
                {
                  pattern: ROLE_ID_REGEX,
                  message: t('userRoles:form.idInvalid'),
                },
              ]}
            >
              <Input placeholder={t('userRoles:form.idPlaceholder')} />
            </Form.Item>
          )}

          <Form.Item name="description" label={t('userRoles:form.description')}>
            <TextArea rows={2} placeholder={t('userRoles:form.descriptionPlaceholder')} />
          </Form.Item>
        </Card>

        <Card
          title={t('userRoles:form.permissions')}
          extra={<span style={{ fontSize: 12, fontWeight: 400 }}>{t('userRoles:form.permissionsHint')}</span>}
          style={{ marginBottom: 24 }}
        >
          <Form.Item name="permissions" initialValue={{}}>
            <RolePermissionsEditor />
          </Form.Item>
        </Card>

        <Space>
          <Button type="primary" htmlType="submit" loading={createMutation.isPending || updateMutation.isPending}>
            {t('userRoles:form.save')}
          </Button>
          <Button onClick={() => navigate('/user-roles')}>{t('userRoles:form.cancel')}</Button>
        </Space>
      </Form>
    </div>
  );
};
